import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ResponsiveDialog from '../common/ResponsiveDialog';
import apiService from '../../services/api.service';

/**
 * Settings > SAP Approval Workflows > E-mail templates. A template decides which PO
 * fields an approver sees (approval e-mail and approval page); each company is linked
 * to one in Company Management. Mirrors HEADER_FIELDS / LINE_COLUMNS of
 * backend/src/services/sapPoApproval.util.ts.
 */

const HEADER_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'vendor', label: 'Vendor' },
  { key: 'total', label: 'Total (with VAT)' },
  { key: 'paymentTerms', label: 'Payment terms' },
  { key: 'postingDate', label: 'Posting date' },
  { key: 'deliveryDate', label: 'Delivery date' },
  { key: 'requiredDate', label: 'Required date' },
  { key: 'vendorRef', label: 'Vendor ref.' },
  { key: 'project', label: 'Project' },
  { key: 'branch', label: 'Branch' },
  { key: 'remarks', label: 'Remarks' },
];

const LINE_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'itemCode', label: 'Item' },
  { key: 'description', label: 'Description' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'uom', label: 'UoM' },
  { key: 'unitPrice', label: 'Price' },
  { key: 'lastPurchasePrice', label: 'Last purchase price (latest GRPO / A/P invoice)' },
  { key: 'lineTotal', label: 'Line total' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'inStock', label: "In stock (line's warehouse)" },
  { key: 'onOrder', label: "Ordered / pipeline (line's warehouse)" },
  { key: 'minInventory', label: "Min. inventory (line's warehouse)" },
  { key: 'project', label: 'Project' },
  { key: 'dim1', label: 'Dimension 1' },
  { key: 'dim2', label: 'Dimension 2' },
  { key: 'dim3', label: 'Dimension 3' },
  { key: 'dim4', label: 'Dimension 4' },
  { key: 'dim5', label: 'Dimension 5' },
  { key: 'glAccount', label: 'G/L account' },
  { key: 'deliveryDate', label: 'Delivery date' },
  { key: 'vatGroup', label: 'VAT group' },
  { key: 'freeText', label: 'Line remarks' },
];

interface Udf { field: string; label: string; linkField?: string; showWhenEmpty?: boolean }
interface Options {
  headerFields: string[];
  lineColumns: string[];
  headerUdfs: Udf[];
  lineUdfs: Udf[];
  showNames: boolean;
  hideEmptyColumns: boolean;
  showHistory: boolean;
  showAttachments: boolean;
  intro: string;
}
interface Template {
  templateid: number;
  templatekey: string;
  name: string;
  description: string;
  options: Options;
  isdefault: boolean;
  isactive: boolean;
  companies: Array<{ id: number; name: string; database: string; viaDefault: boolean }>;
}

const EMPTY: Options = {
  headerFields: ['vendor', 'total', 'postingDate', 'deliveryDate', 'remarks'],
  lineColumns: ['itemCode', 'description', 'quantity', 'uom', 'unitPrice', 'lineTotal'],
  headerUdfs: [],
  lineUdfs: [],
  showNames: true,
  hideEmptyColumns: true,
  showHistory: true,
  showAttachments: true,
  intro: '',
};

const toggle = (list: string[], key: string) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
const errorText = (e: any, fallback: string) => String(e?.response?.data?.message || fallback);

const UdfEditor: React.FC<{ title: string; help: string; value: Udf[]; withLink?: boolean; withShowEmpty?: boolean; onChange: (v: Udf[]) => void }> = ({ title, help, value, withLink, withShowEmpty, onChange }) => (
  <Box>
    <Typography variant="subtitle2">{title}</Typography>
    <Typography variant="caption" color="text.secondary" display="block" mb={1}>{help}</Typography>
    <Stack spacing={1}>
      {value.map((u, i) => (
        <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField size="small" label="SAP field (U_...)" value={u.field} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, field: e.target.value.trim() } : x)))} />
          <TextField size="small" label="Label" value={u.label} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
          {withLink ? (
            <TextField size="small" label="Link field (optional)" value={u.linkField || ''} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, linkField: e.target.value.trim() } : x)))} />
          ) : null}
          {withShowEmpty ? (
            <FormControlLabel
              sx={{ whiteSpace: 'nowrap' }}
              control={<Checkbox size="small" checked={u.showWhenEmpty === true} onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, showWhenEmpty: e.target.checked } : x)))} />}
              label="Show when empty"
            />
          ) : null}
          <IconButton aria-label={`Remove ${u.field || 'field'}`} onClick={() => onChange(value.filter((_x, j) => j !== i))}><DeleteIcon /></IconButton>
        </Stack>
      ))}
      <Box><Button size="small" onClick={() => onChange([...value, { field: 'U_', label: '' }])}>Add field</Button></Box>
    </Stack>
  </Box>
);

const SapApprovalEmailTemplates: React.FC<{ companyId: number | ''; companyName: string }> = ({ companyId, companyName }) => {
  const [templates, setTemplates] = React.useState<Template[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const [editing, setEditing] = React.useState<Template | null>(null);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [form, setForm] = React.useState<{ name: string; description: string; options: Options }>({ name: '', description: '', options: EMPTY });
  const [saving, setSaving] = React.useState(false);
  const [editorError, setEditorError] = React.useState('');

  const [preview, setPreview] = React.useState<{ open: boolean; loading: boolean; title: string; subject: string; html: string; error: string }>({
    open: false, loading: false, title: '', subject: '', html: '', error: '',
  });

  const load = React.useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res: any = await apiService.getSapApprovalEmailTemplates();
      setTemplates(res?.data?.templates || []);
    } catch (e: any) {
      setError(errorText(e, 'Could not load the e-mail templates'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const openEditor = (t: Template | null, copy?: Template) => {
    setEditing(t);
    setEditorError('');
    const src = t || copy;
    setForm({
      name: t ? t.name : copy ? `${copy.name} (copy)` : '',
      description: src?.description || '',
      options: JSON.parse(JSON.stringify(src?.options || EMPTY)),
    });
    setEditorOpen(true);
  };

  const save = async () => {
    setSaving(true); setEditorError('');
    try {
      if (editing) {
        await apiService.updateSapApprovalEmailTemplate(editing.templateid, { name: form.name, description: form.description, options: form.options });
      } else {
        await apiService.createSapApprovalEmailTemplate({ name: form.name, description: form.description, options: form.options });
      }
      setEditorOpen(false);
      await load();
    } catch (e: any) {
      setEditorError(errorText(e, 'Could not save the template'));
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async (title: string, payload: { templateId?: number; options?: Options }) => {
    if (!companyId) { setError('Choose a company at the top of the page to preview with its POs.'); return; }
    setPreview({ open: true, loading: true, title, subject: '', html: '', error: '' });
    try {
      const res: any = await apiService.previewSapApprovalEmail(Number(companyId), payload);
      setPreview({ open: true, loading: false, title, subject: res?.data?.subject || '', html: res?.data?.html || '', error: '' });
    } catch (e: any) {
      setPreview({ open: true, loading: false, title, subject: '', html: '', error: errorText(e, 'Could not build the preview') });
    }
  };

  const act = async (fn: () => Promise<any>, fallback: string) => {
    setError('');
    try { await fn(); await load(); } catch (e: any) { setError(errorText(e, fallback)); }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} mb={1.5}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Choose which PO fields approvers see. Each company is linked to a template in Company Management; companies without one use the default.
          Previews use a real PO approval request of <strong>{companyName || 'the selected company'}</strong> and send nothing.
        </Typography>
        <Button variant="contained" onClick={() => openEditor(null)}>New template</Button>
      </Stack>
      {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {loading ? <CircularProgress size={22} aria-label="Loading templates" /> : null}
      <Stack spacing={1.5}>
        {templates.map((t) => (
          <Paper key={t.templateid} variant="outlined" sx={{ p: 1.5, opacity: t.isactive ? 1 : 0.6 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 700 }}>{t.name}</Typography>
                  {t.isdefault ? <Chip size="small" color="primary" label="Default" /> : null}
                  {!t.isactive ? <Chip size="small" label="Inactive" /> : null}
                </Stack>
                {t.description ? <Typography variant="body2" color="text.secondary">{t.description}</Typography> : null}
                <Typography variant="caption" color="text.secondary" display="block">
                  Header: {t.options.headerFields.map((k) => HEADER_FIELDS.find((h) => h.key === k)?.label || k).join(', ') || '—'}
                  {t.options.headerUdfs.length ? ` + ${t.options.headerUdfs.map((u) => u.label).join(', ')}` : ''}
                  {' · '}Lines: {t.options.lineColumns.length ? t.options.lineColumns.map((k) => LINE_COLUMNS.find((c) => c.key === k)?.label || k).join(', ') : 'none'}
                </Typography>
                <Box mt={0.5}>
                  {t.companies.length ? t.companies.map((c) => (
                    <Tooltip key={c.id} title={`${c.database}${c.viaDefault ? ' – no template chosen, uses the default' : ''}`}>
                      <Chip size="small" variant={c.viaDefault ? 'outlined' : 'filled'} label={c.name} sx={{ mr: 0.5, mb: 0.5 }} />
                    </Tooltip>
                  )) : <Typography variant="caption" color="text.secondary">No company uses it.</Typography>}
                </Box>
              </Box>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <Button size="small" onClick={() => runPreview(t.name, { templateId: t.templateid })}>Preview</Button>
                <Button size="small" onClick={() => openEditor(t)}>Edit</Button>
                <Button size="small" onClick={() => openEditor(null, t)}>Copy</Button>
                {!t.isdefault && t.isactive ? (
                  <Button size="small" onClick={() => act(() => apiService.updateSapApprovalEmailTemplate(t.templateid, { isdefault: true }), 'Could not make it the default')}>Make default</Button>
                ) : null}
                {!t.isdefault ? (
                  <Button size="small" onClick={() => act(() => apiService.updateSapApprovalEmailTemplate(t.templateid, { isactive: !t.isactive }), 'Could not change the template')}>
                    {t.isactive ? 'Deactivate' : 'Activate'}
                  </Button>
                ) : null}
                {!t.isdefault ? (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => { if (window.confirm(`Delete the template "${t.name}"?`)) void act(() => apiService.deleteSapApprovalEmailTemplate(t.templateid), 'Could not delete the template'); }}
                  >
                    Delete
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>

      <ResponsiveDialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="md" fullWidth aria-labelledby="tpl-editor-title">
        <DialogTitle id="tpl-editor-title">{editing ? `Edit template – ${editing.name}` : 'New e-mail template'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth required inputProps={{ maxLength: 100 }} />
              <TextField label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
            </Stack>
            <Box>
              <Typography variant="subtitle2">PO header</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' } }}>
                {HEADER_FIELDS.map((h) => (
                  <FormControlLabel
                    key={h.key}
                    control={<Checkbox checked={form.options.headerFields.includes(h.key)} onChange={() => setForm({ ...form, options: { ...form.options, headerFields: toggle(form.options.headerFields, h.key) } })} />}
                    label={h.label}
                  />
                ))}
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2">Line columns</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Dimensions are named from each company&apos;s SAP dimensions; inactive ones are skipped. Service POs automatically show description, G/L account and amount.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' } }}>
                {LINE_COLUMNS.map((c) => (
                  <FormControlLabel
                    key={c.key}
                    control={<Checkbox checked={form.options.lineColumns.includes(c.key)} onChange={() => setForm({ ...form, options: { ...form.options, lineColumns: toggle(form.options.lineColumns, c.key) } })} />}
                    label={c.label}
                  />
                ))}
              </Box>
            </Box>
            <UdfEditor
              title="SAP user-defined fields – header"
              help="Shown with the PO header when filled (or always, with “—”, when Show when empty is ticked), e.g. U_PR_Number with link field U_PR_Link. Fields with a SAP value list or linked table show the full name (PM → Project Material POs); dates show as dates."
              value={form.options.headerUdfs}
              withLink
              withShowEmpty
              onChange={(v) => setForm({ ...form, options: { ...form.options, headerUdfs: v } })}
            />
            <UdfEditor
              title="SAP user-defined fields – lines"
              help="Extra line columns, e.g. U_Prj_Name."
              value={form.options.lineUdfs}
              onChange={(v) => setForm({ ...form, options: { ...form.options, lineUdfs: v } })}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <FormControlLabel control={<Switch checked={form.options.showNames} onChange={(e) => setForm({ ...form, options: { ...form.options, showNames: e.target.checked } })} />} label="Show names of projects, dimensions and G/L accounts" />
              <FormControlLabel control={<Switch checked={form.options.hideEmptyColumns} onChange={(e) => setForm({ ...form, options: { ...form.options, hideEmptyColumns: e.target.checked } })} />} label="Hide columns that are empty on every line" />
              <FormControlLabel control={<Switch checked={form.options.showHistory} onChange={(e) => setForm({ ...form, options: { ...form.options, showHistory: e.target.checked } })} />} label="Show the approval history" />
              <FormControlLabel control={<Switch checked={form.options.showAttachments} onChange={(e) => setForm({ ...form, options: { ...form.options, showAttachments: e.target.checked } })} />} label="List / attach the PO attachments" />
            </Box>
            <TextField
              label="Extra message under the greeting (optional)"
              value={form.options.intro}
              onChange={(e) => setForm({ ...form, options: { ...form.options, intro: e.target.value.slice(0, 1000) } })}
              multiline
              minRows={2}
              fullWidth
            />
            {editorError ? <Alert severity="error">{editorError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => runPreview(form.name || 'Unsaved template', { options: form.options })}>Preview</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || form.name.trim().length < 3}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </ResponsiveDialog>

      <ResponsiveDialog open={preview.open} onClose={() => setPreview((p) => ({ ...p, open: false }))} maxWidth="lg" fullWidth aria-labelledby="tpl-preview-title">
        <DialogTitle id="tpl-preview-title">Preview – {preview.title}</DialogTitle>
        <DialogContent dividers>
          {preview.loading ? (
            <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={20} /><Typography variant="body2">Reading a PO from SAP…</Typography></Stack>
          ) : null}
          {preview.error ? <Alert severity="error">{preview.error}</Alert> : null}
          {preview.html ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}><strong>Subject:</strong> {preview.subject}</Typography>
              <Box
                component="iframe"
                title="E-mail preview"
                sandbox=""
                srcDoc={preview.html}
                sx={{ width: '100%', height: '70vh', border: '1px solid #e2e8f0', borderRadius: 1, bgcolor: '#fff' }}
              />
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview((p) => ({ ...p, open: false }))}>Close</Button>
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default SapApprovalEmailTemplates;
